import React, { createContext, useEffect, useRef, useState } from 'react';
import { LoginCallback, SecureRoute, useOktaAuth } from '@okta/okta-react';
import { Route, Switch } from "react-router-dom";
import "@deere/ux.uxframe-core/dist/css/uxframe.min.css";
import {Alert, Box, Input, jdGreen, ThemeProvider, ToastProvider} from '@deere/fuel-react';
import { ThemeProvider as ThemeProviderV4 } from '@material-ui/core/styles';
import { AEBreadCrumbs } from '../shared/BreadCrumbs';
import { NoAccessUser } from "../shared/NoAccessUser";
import routes from "../routes";
import { useUserInfo } from "../shared/UserInfo";
import { useAuthConfig } from "../shared/helper";
import EditIcon from "@material-ui/icons/Edit";
import IconButton from "@material-ui/core/IconButton";
import { MultiUseModal } from "../shared/modal/MultiUseModal";
import Stack from '@mui/material/Stack';
import { FormatTextOutput } from "../shared/styles/FormatTextOutput";
import {RadioGroup, FormControlLabel, Radio, createGenerateClassName, StylesProvider} from '@material-ui/core';
import {adaptV4Theme} from "@mui/material";

export const UserContext = createContext(null);
export const Main = () => {
    let authAxios = useAuthConfig();
    const { authState } = useOktaAuth();
    const checkCountRef = useRef(0);
    const maxChecks = 5;
    const info = useUserInfo();
    const roles = info && info.roles;
    const isSupplier = info && info.isSupplier;
    const isInternal = info && info.isInternal;
    const [onEditView, setOnEditView] = useState(false);
    const [bannerAnnouncement, setBannerAnnouncement] = useState({});
    const [bannerType, setBannerType] = useState('');
    const [updatedBannerAnnouncement, setUpdatedBannerAnnouncement] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState('info');
    const themev4 = adaptV4Theme(jdGreen);


    useEffect(() => {
        setUpdatedBannerAnnouncement(bannerAnnouncement)
    }, [modalOpen, bannerAnnouncement]);

    const getCurrentAnnouncementData = async () => {
        try {
            const res = await authAxios.get(`/announcements/current`);
            const data = res.data;
            processAnnouncementData(data);
        } catch (error) {
            console.log(error);
        }
    };

    const postBannerAnnouncement = async (data) => {
        const postJSON = {
            title: updatedBannerAnnouncement.title,
            announcement: updatedBannerAnnouncement.announcement,
            announcementType: updatedBannerAnnouncement.announcementType,
            user: info.userId
        }
        try {
            const res = await authAxios.post(`/announcements`, postJSON);
            processAnnouncementData(res.data);
            await authAxios.delete(`/announcements/${data.id}`);
        } catch (error) {
            console.log(error);
        }
    }

    const processAnnouncementData = data => {
        let latestBannerAnnouncement = {};
        if (data.length > 0) {
            latestBannerAnnouncement = data
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .filter(announcement => (
                    announcement.announcementType === "success" ||
                    announcement.announcementType === "warning" ||
                    announcement.announcementType === "critical" ||
                    announcement.announcementType === "informational"
                ))[0];
        } else {
            latestBannerAnnouncement = data
        }

        // Convert the announcement type to the Fuel-React Alert variant
        if (latestBannerAnnouncement.announcementType === "critical") {
            setBannerType('error');
        } else if (latestBannerAnnouncement.announcementType === "informational") {
            setBannerType('info');
        } else {
            setBannerType(latestBannerAnnouncement.announcementType);
        }
        setBannerAnnouncement(latestBannerAnnouncement);
    }

    useEffect(() => {
        if (info) {
            getCurrentAnnouncementData();
        }
        return () => {
        }// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [info]);

    useEffect(() => {
        const checkTokenValidity = async () => {
            await new Promise(r => setTimeout(r, 5000));

            const tokenInStorage = JSON.parse(localStorage.getItem('okta-token-storage'))?.accessToken;

            if ((!authState.accessToken || !tokenInStorage) && !authState.isPending) {
                localStorage.removeItem('okta-token-storage');
                window.location.reload();
            }

            checkCountRef.current += 1;
            if (checkCountRef.current >= maxChecks) {
                clearInterval(intervalId);
            }
        };

        checkTokenValidity();

        const intervalId = setInterval(checkTokenValidity, 15000);
        return () => clearInterval(intervalId);
    }, [authState]);

    const onSubmit = async () => {
        if(updatedBannerAnnouncement.announcement.trim().length > 0) {
            await postBannerAnnouncement(updatedBannerAnnouncement);
            setModalOpen(false);
        }
    };

    const handleEdit = () => {
        setModalOpen(true);
    };

    const handleVariantChange = (event) => {
        setSelectedVariant(event.target.value);
        setUpdatedBannerAnnouncement({
            ...updatedBannerAnnouncement,
            announcementType: event.target.value
        });
    };

    const generateClassName = createGenerateClassName({
        // By enabling this option, if you have non-MUI elements (for example `<div />`)
        // using MUI classes (for example `.MuiButton`) they will lose styles.
        // Make sure to convert them to use `styled()` or `<Box />` first.
        disableGlobal: true,
        // Class names will receive this seed to avoid name collisions.
        seed: 'mui-jss',
    });

    return (
        <div id="content">
            <StylesProvider generateClassName={generateClassName}>
                <ThemeProviderV4 theme={themev4}>
            <ThemeProvider baseline={false}>
                {bannerAnnouncement && bannerAnnouncement.announcement && (
                    <Alert variant={bannerType} title={bannerAnnouncement.title}>
                        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <FormatTextOutput text={bannerAnnouncement.announcement} />
                            {onEditView && (
                                <IconButton onClick={() => handleEdit()} aria-label="edit">
                                    <EditIcon />
                                </IconButton>
                            )}
                        </span>
                    </Alert>
                )}
                {modalOpen && (
                    <Stack maxHeight={0}>
                        <MultiUseModal
                            isOpen={modalOpen}
                            modalTitle={"Edit Banner Announcement"}
                            announcementTitle={bannerAnnouncement.title}
                            announcement={bannerAnnouncement.announcement}
                            onSubmit={onSubmit}
                            handleModalClose={() => setModalOpen(false)}
                        >
                            <strong>Banner Type</strong>
                            <RadioGroup value={selectedVariant} onChange={handleVariantChange}>
                                <FormControlLabel value="success" control={<Radio />} label={<Alert variant="success" />}/>
                                <FormControlLabel value="warning" control={<Radio />} label={<Alert variant="warning" />}/>
                                <FormControlLabel value="critical" control={<Radio />} label={<Alert variant="error" />}/>
                                <FormControlLabel value="informational" control={<Radio />} label={<Alert variant="info" />}/>
                            </RadioGroup>
                            < br />
                            <strong>Title</strong>
                            <Input
                                onChange={(event) => {
                                    setUpdatedBannerAnnouncement({
                                        ...updatedBannerAnnouncement,
                                        title: event.target.value
                                    });
                                }}
                                defaultValue={bannerAnnouncement.title}
                                label={"Title"}
                                placeholder={"Type a title"}
                                fullWidth
                                inputProps={
                                    { maxLength: 50 }
                                }
                            />
                            <br />
                            <br />
                            <strong>Announcement*</strong>
                            <Input
                                onChange={(event) => {
                                    setUpdatedBannerAnnouncement({
                                        ...updatedBannerAnnouncement,
                                        announcement: event.target.value
                                    });
                                }}
                                defaultValue={bannerAnnouncement.announcement}
                                label={"Text Value"}
                                multiline={true}
                                aria-multiline={true}
                                placeholder={"Type an announcement"}
                                minRows={4}
                                fullWidth
                                inputProps={
                                    { maxLength: 750 }
                                }
                            />
                        </MultiUseModal>
                    </Stack>
                )}
                <UserContext.Provider value={{...info, onEditView, setOnEditView}}>
                    <Route path="/login" exact component={LoginCallback} />
                    <Switch>
                        {routes.map(({ path, name, Component, role: permittedRole }, key) => (
                            <SecureRoute
                                exact
                                path={path}
                                key={key}
                                render={props => {
                                    const crumbs = routes
                                        .filter(({ path }) => props.match.path.includes(path))
                                        .map(({ path, ...otherProps }) => ({
                                            path: Object.keys(props.match.params).length
                                                ? Object.keys(props.match.params).reduce(
                                                    (path, param) =>
                                                        path.replace(`:${param}`, props.match.params[param]),
                                                    path
                                                )
                                                : path,
                                            ...otherProps
                                        })).reverse();
                                    return (
                                        <Box p={5}>{
                                            (function() {
                                                // eslint-disable-next-line no-mixed-operators
                                                if ((permittedRole !== "" && permittedRole !== "ALL") && (!roles || (roles && roles.length > 0 && !roles.includes(permittedRole))) || (permittedRole !== "ALL" && isSupplier)) {
                                                    return <NoAccessUser />;
                                                // eslint-disable-next-line no-mixed-operators
                                                } else if ((permittedRole !== "" && roles && roles.length > 0 && roles.includes(permittedRole) && isInternal) || ((permittedRole === "" && isInternal) || permittedRole === "ALL")) {
                                                    return (
                                                        <ToastProvider>
                                                            <AEBreadCrumbs crumbs={crumbs} currentPath={props.match.url} />
                                                            <Component {...props} name={name} />
                                                        </ToastProvider>
                                                    );
                                                }
                                            })()
                                        }
                                        </Box>
                                    );
                                }}
                            />
                        ))}
                    </Switch>
                </UserContext.Provider>
            </ThemeProvider>
                </ThemeProviderV4>
            </StylesProvider>
        </div>
    );
}
